import rest from '@modules/rest'
import upload from '@modules/upload'
import { mapCustomFieldValues } from '@convenia/common-organisms/SystemFields/helpers'
import { PERSONAL_DOCUMENTS_AREA } from '@content/consts/Documents'
import {
  mapSystemFields,
  mapCustomFields,
  mapCPFInput,
  mapRGInput,
  mapDriverLicenseInput,
  mapReservistInput,
  mapElectoralCardInput,
  mapCTPSInput
} from '@convenia/employee-organisms/SystemFields/Documents/content/mappers'
import { getUploadEndpoint } from '@modules/fileHelpers'

export const getCustomDocuments = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/custom-field-values/documents/admission`

    const { data } = await rest.get(url)

    return [ null, data ]
  } catch (err) {
    return [ err, null ]
  }
}

export const getSystemFields = async ({ employeeId, companyId }) => {
  try {
    const url = `/companies/proxy/companies/${companyId}/employees/${employeeId}/fields/admission`
    const { data } = await rest.get(url, { params: { area: PERSONAL_DOCUMENTS_AREA.LABEL } })
    const fields = data?.sections || []

    return [ null, {
      systemFields: mapSystemFields(fields),
      customFields: mapCustomFields(fields),
    } ]
  } catch (err) {
    return [ err, null ]
  }
}

export const getGeneralRegisters = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/general-registers/admission`

    const { data } = await rest.get(url)

    return [ null, data ]
  } catch (err) {
    if (err?.response?.status === 404) return [ null, null ]

    return [ err, null ]
  }
}

export const updateGeneralRegisters = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/general-registers/admission`
    const body = mapRGInput({ data, schema })

    const response = await rest.post(url, { body })

    return [ null, response?.data || data ]
  } catch (err) {
    return [ err ]
  }
}

export const updateDriverLicense = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/driver-license/admission`
    const body = mapDriverLicenseInput({ data, schema })

    const response = await rest.post(url, { body })

    return [ null, response?.data || data ]
  } catch (err) {
    return [ err ]
  }
}

export const getDriverLicense = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/driver-license/admission`

    const { data } = await rest.get(url)

    return [ null, data ]
  } catch (err) {
    if (err?.response?.status === 404) return [ null, null ]

    return [ err, null ]
  }
}

export const getCpf = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/cpf/admission`

    const { data } = await rest.get(url)

    return [ null, data ]
  } catch (err) {
    if (err?.response?.status === 404) return [ null, null ]

    return [ err, null ]
  }
}

export const updateCpf = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/cpf/admission`
    const body = mapCPFInput({ data, schema })

    const response = await rest.post(url, { body })

    return [ null, response?.data || data ]
  } catch (err) {
    return [ err ]
  }
}

export const updateReservist = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/reservists/admission`
    const body = mapReservistInput({ data, schema })

    const response = await rest.post(url, { body })

    return [ null, response?.data || data ]
  } catch (err) {
    return [ err ]
  }
}

export const getReservist = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/reservists/admission`

    const { data } = await rest.get(url)

    return [ null, data ]
  } catch (err) {
    if (err?.response?.status === 404) return [ null, null ]

    return [ err, null ]
  }
}

export const getElectoralCard = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/electoral-card/admission`

    const { data } = await rest.get(url)

    return [ null, data ]
  } catch (err) {
    if (err?.response?.status === 404) return [ null, null ]

    return [ err, null ]
  }
}

export const updateElectoralCard = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/electoral-card/admission`
    const body = mapElectoralCardInput({ data, schema })

    const response = await rest.post(url, { body })

    return [ null, response?.data || data ]
  } catch (err) {
    return [ err ]
  }
}

export const getCtps = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/ctps/admission`

    const { data } = await rest.get(url)

    return [ null, data ]
  } catch (err) {
    if (err?.response?.status === 404) return [ null, null ]

    return [ err, null ]
  }
}

export const updateCtps = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/ctps/admission`
    const body = mapCTPSInput({ data, schema })

    const response = await rest.post(url, { body })

    return [ null, response?.data || data ]
  } catch (err) {
    return [ err ]
  }
}

export const updateCustomDocument = async (data, params) => {
  try {
    const sectionId = data.id
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/sections/${sectionId}/custom-field-values/admission`
    const body = {
      custom_fields: mapCustomFieldValues({
        data,
        schema,
        sectionId,
      })
    }

    await rest.put(url, { body })

    return [ null ]
  } catch (err) {
    return [ err ]
  }
}

export const uploadDocumentFiles = async ({ id, files, employeeId, type }) => {
  try {
    const endpoint = getUploadEndpoint(employeeId, id, type)

    await upload({ files, endpoint })

    return [ null, true ]
  } catch (err) {
    return [ err, null ]
  }
}
